import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import { authAPI, e2eeAPI } from '../utils/api';
import { generateKeyPair, hasStoredKeys, getStoredPublicKey, isE2EESupported } from '../utils/e2ee';
import { track, identifyUser, getFCMToken, onForegroundMessage } from '../utils/firebase';
import { showNotification } from '../utils/notifications';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// connectionState: 'disconnected' | 'connecting' | 'connected' | 'reconnecting'
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [e2eeReady, setE2eeReady] = useState(false);
  const socketRef = useRef(null);
  const reconnectAttempts = useRef(0);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await authAPI.getMe();
          setUser(response.data);
          initSocket(token);
          initE2EE();
          registerFCMToken(token);
        } catch (error) {
          console.error('[Auth] Init failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };
    initAuth();

    // Listen for FCM foreground messages
    const unsubFCM = onForegroundMessage((payload) => {
      const { notification, data } = payload;
      if (notification) {
        showNotification(notification.title, {
          body: notification.body,
          tag: data?.tag || 'fcm-foreground',
          data: data,
        });
      }
    });

    return () => {
      if (typeof unsubFCM === 'function') unsubFCM();
    };
    return () => {
      if (socketRef.current?.connected) socketRef.current.disconnect();
    };
  }, []);

  // ── Wake-up reconnect: when tab becomes visible, ensure socket is connected + authenticated ──
  const lastWakeRef = useRef(0);
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      // Debounce: ignore if last wake was < 2s ago
      if (now - lastWakeRef.current < 2000) return;
      lastWakeRef.current = now;

      const sock = socketRef.current;
      const token = localStorage.getItem('token');
      if (!sock || !token) return;

      if (!sock.connected) {
        console.log('[Socket] Tab visible — forcing reconnect');
        sock.connect();
      } else {
        // Socket appears connected but may have lost auth state after long background
        console.log('[Socket] Tab visible — re-authenticating');
        sock.emit('authenticate', { token });
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const initSocket = useCallback((token) => {
    if (socketRef.current?.connected) socketRef.current.disconnect();

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    setConnectionState('connecting');

    const sock = io(BACKEND_URL, {
      path: '/api/socket.io',
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      randomizationFactor: 0.5,
      timeout: 20000,
      transports: ['websocket'],
    });

    sock.on('connect', () => {
      console.log('[Socket] Connected:', sock.id);
      setIsConnected(true);
      setConnectionState('connected');
      reconnectAttempts.current = 0;
      sock.emit('authenticate', { token });
    });

    sock.on('disconnect', (reason) => {
      console.warn('[Socket] Disconnected:', reason);
      setIsConnected(false);
      setConnectionState('disconnected');
      if (reason === 'io server disconnect') {
        sock.connect();
      }
    });

    sock.on('reconnect_attempt', (attempt) => {
      reconnectAttempts.current = attempt;
      setConnectionState('reconnecting');
      console.log(`[Socket] Reconnect attempt ${attempt}`);
    });

    sock.on('reconnect', () => {
      console.log('[Socket] Reconnected — re-authenticating');
      setConnectionState('connected');
      setIsConnected(true);
      sock.emit('authenticate', { token });
    });

    sock.on('connect_error', (err) => {
      console.error('[Socket] Connection error:', err.message);
      setConnectionState('reconnecting');
    });

    socketRef.current = sock;
    setSocket(sock);
  }, []);

  // ── E2EE key setup (runs after auth) ──
  const initE2EE = useCallback(async () => {
    if (!isE2EESupported()) {
      console.warn('[E2EE] Not supported in this browser');
      return;
    }
    try {
      const hasKeys = await hasStoredKeys();
      if (hasKeys) {
        // Keys exist locally — ensure they're uploaded to backend
        const publicKeyJwk = await getStoredPublicKey();
        if (publicKeyJwk) {
          try { await e2eeAPI.uploadPublicKey(publicKeyJwk); } catch (e) { console.warn('[E2EE] Key upload error (may already exist):', e); }
        }
      } else {
        // Generate new key pair
        console.log('[E2EE] Generating new key pair...');
        const publicKeyJwk = await generateKeyPair();
        await e2eeAPI.uploadPublicKey(publicKeyJwk);
        console.log('[E2EE] Key pair generated and uploaded');
      }
      setE2eeReady(true);
    } catch (e) {
      console.error('[E2EE] Init error:', e);
    }
  }, []);

  const registerFCMToken = async (authToken) => {
    // Register web FCM token (browser)
    try {
      const fcmToken = await getFCMToken();
      if (fcmToken) {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/push/fcm-register`, 
          { fcm_token: fcmToken, token_type: 'web' },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      }
    } catch (e) {}
    // Register native FCM token (Capacitor Android)
    try {
      const nativeToken = window.__NATIVE_FCM_TOKEN__;
      if (nativeToken) {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/push/fcm-register`, 
          { fcm_token: nativeToken, token_type: 'native' },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        console.log('[Auth] Native FCM token registered on login');
      }
    } catch (e) {}
  };

  const login = async (credentials) => {
    const response = await authAPI.login(credentials);
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    initSocket(token);
    initE2EE();
    track.login();
    identifyUser(userData.id, { name: userData.name, email: userData.email, role: userData.role });
    registerFCMToken(token);
    return response.data;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    track.signup();
    return response.data;
  };

  const logout = () => {
    track.logout();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (socketRef.current?.connected) socketRef.current.disconnect();
    socketRef.current = null;
    setUser(null);
    setSocket(null);
    setIsConnected(false);
    setConnectionState('disconnected');
  };

  return (
    <AuthContext.Provider value={{ user, loading, socket, isConnected, connectionState, e2eeReady, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
